import { ReactNode } from 'react';
import { MessageSquare, FileText, BarChart3, LinkIcon, Inbox, AlertCircle } from 'lucide-react';
import { cn } from '@/lib/cn';

interface EmptyStateProps {
  icon?: ReactNode;
  title: string;
  description: string;
  action?: {
    label: string;
    onClick: () => void;
  };
  className?: string;
}

/**
 * EmptyState - Generic empty state component
 *
 * Usage:
 * <EmptyState
 *   icon={<MessageSquare />}
 *   title="No conversations"
 *   description="Select a conversation to get started"
 * />
 */
export function EmptyState({
  icon,
  title,
  description,
  action,
  className,
}: EmptyStateProps) {
  return (
    <div className={cn('flex items-center justify-center p-8', className)}>
      <div className="text-center max-w-md">
        {icon && (
          <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-muted mb-4">
            <div className="text-muted-foreground">{icon}</div>
          </div>
        )}
        <h3 className="text-base font-semibold mb-2">{title}</h3>
        <p className="text-sm text-muted-foreground mb-4">{description}</p>
        {action && (
          <button
            onClick={action.onClick}
            className="px-4 py-2 rounded-lg text-sm font-medium bg-primary text-primary-foreground hover:bg-primary/90"
          >
            {action.label}
          </button>
        )}
      </div>
    </div>
  );
}

/**
 * NoConversationSelected - Empty state for no conversation
 */
export function NoConversationSelected() {
  return (
    <EmptyState
      icon={<Inbox className="h-6 w-6" />}
      title="No conversation selected"
      description="Select a conversation from Front to analyze and generate drafts"
    />
  );
}

/**
 * NoDraftsGenerated - Empty state for when no drafts exist yet
 */
export function NoDraftsGenerated({ onGenerate }: { onGenerate: () => void }) {
  return (
    <EmptyState
      icon={<FileText className="h-6 w-6" />}
      title="No draft yet"
      description="Answer the questions above to generate an AI-powered draft response"
      action={{
        label: 'Generate Draft',
        onClick: onGenerate,
      }}
    />
  );
}

/**
 * NoAnalyticsData - Empty state for analytics with no data
 */
export function NoAnalyticsData() {
  return (
    <EmptyState
      icon={<BarChart3 className="h-6 w-6" />}
      title="No analytics data"
      description="Start using Crewflow to generate drafts and see analytics appear here"
    />
  );
}

/**
 * NoIntegrationsConnected - Empty state for integrations
 */
export function NoIntegrationsConnected({ onConnect }: { onConnect: () => void }) {
  return (
    <EmptyState
      icon={<LinkIcon className="h-6 w-6" />}
      title="No integrations connected"
      description="Connect your help center to enable AI-powered draft generation with relevant articles"
      action={{
        label: 'Connect Help Center',
        onClick: onConnect,
      }}
    />
  );
}

/**
 * NoMessagesInConversation - Empty state for empty conversation
 */
export function NoMessagesInConversation() {
  return (
    <EmptyState
      icon={<MessageSquare className="h-6 w-6" />}
      title="No messages"
      description="This conversation doesn't have any messages yet"
    />
  );
}

/**
 * NoChatHistory - Empty state for chat interface
 */
export function NoChatHistory() {
  return (
    <div className="flex items-center justify-center p-6 text-center">
      <div className="max-w-sm">
        <MessageSquare className="h-8 w-8 text-muted-foreground mx-auto mb-3" />
        <p className="text-sm text-muted-foreground">
          Ask questions or request changes to the draft
        </p>
      </div>
    </div>
  );
}

/**
 * SearchEmptyState - Empty state for search results
 */
export function SearchEmptyState({ query }: { query: string }) {
  return (
    <EmptyState
      icon={<AlertCircle className="h-6 w-6" />}
      title="No results found"
      description={`No results found for "${query}". Try a different search term.`}
    />
  );
}

export default EmptyState;
